import { gql } from '@apollo/client';
import React, { FunctionComponent } from 'react';
import { Trans } from 'react-i18next';
import UsersSummary from '@justgiving/users-summary';
import '@justgiving/users-summary/src/UsersSummary.scss';
import { Track } from '@justgiving/react-tracking';

import PATHS from '../../constants/paths';
import './MembersMini.scss';
import { LeaderboardMembersMiniFragment } from '../../../types/__generated__';
import usePageQuery, {
  buildQuery,
  UsePageQueryResult,
} from '../../hooks/usePageQuery';
import { makePhotoUrl } from '../../utils/makePhotoUrl';

const FRAGMENT = buildQuery(gql`
  fragment LeaderboardMembersMini on Page {
    id
    legacyId
    membershipPolicy
    fundraisers: leaderboard(type: FUNDRAISERS, first: 3) {
      edges {
        node {
          title
          status
          legacyId
          id
          url
          slug
          owner {
            id
            avatar
            name
          }
          donations(first: 1) {
            totalCount
          }
          donationSummary {
            totalAmount {
              value
              currencyCode
            }
          }
          targetWithCurrency {
            value
          }
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
      totalCount
    }
  }
`);

const LeaderBoardMembersMini: FunctionComponent = () => {
  const result = usePageQuery<LeaderboardMembersMiniFragment>(FRAGMENT);

  if (!UsePageQueryResult.hasData(result)) {
    return null;
  }

  const { fundraisers, membershipPolicy, legacyId } = result.data;
  const membersCount =
    (fundraisers && fundraisers.edges && fundraisers.totalCount) || 0;

  const membersList =
    (fundraisers &&
      fundraisers.edges.map(member => ({
        id: member.node.id,
        picture:
          (member.node.owner &&
            member.node.owner.avatar &&
            makePhotoUrl(member.node.owner.avatar)) ||
          undefined,
      }))) ||
    [];

  return (
    <UsersSummary usersList={membersList} avatarSize="medium" hasBorder>
      <div className="jg-space-mlxs jg-pages-membersmini-content jg-text--regular">
        <span className="jg-pages-membersmini-label">
          <Trans i18nKey="team.userLabel" count={membersCount}>
            {{ count: membersCount }} team member
          </Trans>
        </span>
        {membershipPolicy === 'open' && (
          <Track>
            {track => (
              <a
                onClick={() =>
                  track({
                    action_name: 'link click',
                    event_value: 'join the team',
                    sub_section: 'top',
                  })
                }
                className="jg-pages-membersmini-link"
                href={`${PATHS.JOIN_URL}${legacyId}`}
              >
                <Trans i18nKey="members.join">Join the team</Trans>
              </a>
            )}
          </Track>
        )}
      </div>
    </UsersSummary>
  );
};

export default LeaderBoardMembersMini;
